import { motion } from "motion/react";
import { useLocation, useOutletContext } from "react-router-dom";
import { AnimatedDivider } from "../components/AnimatedDivider";
import { useState, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { TacitonicRadio } from "../components/TacitonicRadio";
import { TextScramble } from "../components/TextScramble";
import { MagneticButton } from "../components/MagneticButton";

import { LoadingSpinner } from "../components/LoadingSpinner";

function WhiteBar() {
  return (
    <motion.div 
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.5, ease: [0.19, 1, 0.22, 1] }}
      className="h-[3px] w-full bg-white my-16 origin-left" 
    />
  );
}

function SpotifyEmbed({ url, title }: { url: string; title: string }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const embedUrl = url.replace("open.spotify.com", "open.spotify.com/embed").split("?")[0] + "?utm_source=generator&theme=0";

  return (
    <div className="flex flex-col border-2 border-white/20 hover:border-white transition-all duration-300 p-4 md:p-8 group hover-target cursor-none">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 border-b-[3px] border-white pb-4 gap-4">
        <h3 className="text-[20px] md:text-[31px] font-bold uppercase tracking-tighter font-display">{title}</h3>
        <MagneticButton>
          <a href={url} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-2 md:gap-3 border-2 border-white/20 hover:border-transparent pl-5 pr-4 py-3 md:pl-6 md:pr-4 md:py-3 hover:bg-white hover:text-black transition-all duration-300 hover-target font-mono text-[10px] md:text-[12px] uppercase tracking-[0.2em] font-bold w-fit relative overflow-hidden">
            <span className="relative z-10"><TextScramble text="Listen" /></span> <ArrowUpRight className="text-[#9af104] opacity-100 transition-opacity relative z-10 w-3.5 h-3.5 md:w-4 md:h-4" />
          </a>
        </MagneticButton>
      </div>
      <div className="relative w-full overflow-hidden bg-transparent" style={{ minHeight: "352px" }}>
        {/* Skeleton Loader */}
        {!isLoaded && <LoadingSpinner />}
        <iframe 
          style={{ borderRadius: "0px" }} 
          src={embedUrl} 
          width="100%" 
          height="352" 
          frameBorder="0" 
          allowFullScreen 
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          className={`grayscale-[0.5] hover:grayscale-0 transition-all duration-700 cursor-none ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        ></iframe>
      </div>
    </div>
  );
}

import { Helmet } from "react-helmet-async";

export function Discography() {
  const location = useLocation();
  const { hasLoaded, handleNavClick, isScrolledPop } = useOutletContext<{ hasLoaded: boolean, handleNavClick: (e: React.MouseEvent<HTMLAnchorElement>, href: string) => void, isScrolledPop?: boolean }>();
  const [isDocking, setIsDocking] = useState(() => Boolean(hasLoaded && location.state?.fromNav));

  useEffect(() => {
    if (isDocking) {
      const timer = setTimeout(() => setIsDocking(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isDocking]);

  return (
    <>
      <Helmet>
        <title>Discography • Atharva Dhekne | tacitonic</title>
        <meta name="description" content="Explore the mastering, immersive mixing, and engineering discography of Atharva Dhekne (tacitonic). Featuring Dolby Atmos, stereo mastering, and production credits." />
        <meta name="robots" content="index, follow, max-image-preview:none" />
        <meta name="googlebot" content="index, follow, max-image-preview:none" />
        <link rel="canonical" href="https://tacitonic.com/discography" />
        <meta property="og:title" content="Discography • Atharva Dhekne | tacitonic" />
        <meta property="og:description" content="Explore the mastering, immersive mixing, and engineering discography of Atharva Dhekne (tacitonic)." />
        <meta property="og:url" content="https://tacitonic.com/discography" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Discography • Atharva Dhekne (tacitonic)",
            "url": "https://tacitonic.com/discography",
            "description": "Selected stereo mastering, immersive audio, and music production credits by Atharva Dhekne.",
            "about": {
              "@id": "https://tacitonic.com/#person"
            }
          })}
        </script>
      </Helmet>
      <main className="pt-40 pb-32 px-6 max-w-7xl mx-auto min-h-screen">
      <div className="mb-16 pb-8 relative">
        <motion.h1 
          layoutId={isScrolledPop ? undefined : `page-title-Discography`}
          initial={(!hasLoaded || isScrolledPop) ? { opacity: 0, scale: 0.95, y: 10 } : undefined}
          animate={{ opacity: 1, scale: 1, y: 0, color: "#FEFDF9" }}
          className="text-[31px] md:text-[52px] lg:text-[64px] font-bold tracking-tighter uppercase leading-none font-display w-fit origin-left text-white"
          transition={{ 
            duration: 1.2,
            ease: [0.16, 1, 0.3, 1],
            layout: { duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }
          }}
        >
          Discography
        </motion.h1>
        {!isDocking && (
          <div className="absolute bottom-0 left-0 w-full">
            <AnimatedDivider className="h-[3px] bg-white" />
          </div>
        )}
      </div>

      <section className="sr-only" aria-label="Discography Overview">
        <h2>Selected Engineering, Mastering, and Immersive Audio Credits by Atharva Dhekne (tacitonic)</h2>
        <p>
          Explore the official engineering and production discography of Atharva Dhekne (alias tacitonic), Mastering and Immersive Audio Engineer at Sterling Sound and Eventide Audio in New York City. Curated across four catalogs—Mastering Production, Immersive Engineering, Stereo Mastering, and Immersive Production—this collection spans over 700 chart-topping releases and 10 billion global streams across Lossless Digital, Compact Disc (CD), Vinyl, Analog Tape, Cassette, Apple Digital Masters, Dolby Atmos, Sony 360 Reality Audio, and IAMF Eclipsa Audio.
        </p>
        <p>
          Selected artist credits include Keith Richards, The Rolling Stones, Paul Simon, Billy Joel, Carlos Santana, Joan Jett, Andrea Bocelli, Josh Groban, Pharrell Williams, David Guetta, Dua Lipa, Rihanna, Bad Bunny, Future, Metro Boomin, J. Cole, Don Toliver, Central Cee, Latto, BigXThaPlug, Young Nudy, ROSÉ, JENNIE, Jisoo, BLACKPINK, j-hope, Jin, LE SSERAFIM, ENHYPEN, ILLIT, KATSEYE, MEOVV, MINNIE, Green Day, The Black Keys, Turnstile, Twenty One Pilots, Tame Impala, Florence + The Machine, Glass Animals, The National, Noah Kahan, Leon Bridges, Kacey Musgraves, Miranda Lambert, Jelly Roll, Tedeschi Trucks Band, Branford Marsalis, Dave Holland, Bill Frisell, Robert Cray, Dinosaur Jr., Goo Goo Dolls, Three Days Grace, Europe, OneRepublic, Maroon 5, Robbie Williams, Selena Gomez, Miley Cyrus, Shawn Mendes, Benny Blanco, Ava Max, Bebe Rexha, Robyn, Slayyyter, Laufey, keshi, Tom Odell, Sam Fender, Steve Aoki, Rauw Alejandro, Lin-Manuel Miranda, Jimmy Webb, Hermanos Gutiérrez, Metric, Poolside, Balu Brigada, Kate Hudson, Diljit Dosanjh, Karan Aujla, AP Dhillon, Shubh, Avadhoot Gupte, Clinton Cerejo, and Shor Police.
        </p>
      </section>

      <div className={`transition-opacity duration-700 delay-300 ${isDocking ? 'opacity-0' : 'opacity-100'}`}>
        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <SpotifyEmbed 
            title="Mastering Production" 
            url="https://open.spotify.com/playlist/6Dhj9838l50lWCgNXR9rip?si=c9aa08fb159b4224" 
          />
          <SpotifyEmbed 
            title="Immersive Engineering" 
            url="https://open.spotify.com/playlist/7Ir6kz4V3W8v4UVbESbuZN?si=dfab4117ee234bd7" 
          />
        </div>
        
        <WhiteBar />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <SpotifyEmbed 
            title="Stereo Mastering" 
            url="https://open.spotify.com/playlist/64CoNPLPVgppIjLqXLKi69?si=160fe37651974783" 
          />
          <SpotifyEmbed 
            title="Immersive Production" 
            url="https://open.spotify.com/playlist/2DXLXdXSzCleyal6pZBnTK?si=8ab8f7a8eb7545a7" 
          />
        </div>
      </motion.div>
      </div>
    </main>
    </>
  );
}
